import { defineStore } from 'pinia'
import NotificationStore, {
  NotificationState,
  ShowNotificationPayloadI,
} from '@/typescript/types/stores/NotificationStore'
import { getLocalStorageItem, removeLocalStorageItem, setLocalStorageItem } from '@/utils/index'
import { NETWORK_ERROR_MESSAGE, TIMEOUT_ERROR_MESSAGE } from '@/entities/errors/api_errors/NetworkError'

export const MASQUERADE_DISMISSED_KEY = 'masqueradeNotificationDismissed'
export const MASQUERADE_NOTIFICATION_CONTENT = 'You are currently masquerading as a client.'

const CONNECTION_ERROR_MESSAGES = [NETWORK_ERROR_MESSAGE, TIMEOUT_ERROR_MESSAGE]

/**
 * A lost connection is not the page's error, so it is always raised as a toast, whichever call
 * site caught it — most of them prefix it as `Error: ${e.message}`, which the toast's error tone
 * already says, so the bare sentence is kept.
 */
function asConnectionToast(data: ShowNotificationPayloadI): ShowNotificationPayloadI {
  const message = CONNECTION_ERROR_MESSAGES.find(item => data.content?.includes(item))
  return message ? { ...data, content: message, asToast: true } : data
}

export const useNotificationStore: () => NotificationStore = defineStore('notification', {
  state: () =>
    ({
      isShow: false,
      type: '',
      content: '',
      notificationList: [],
      notificationCount: 0,
      masqueradeNotificationDismissed: getLocalStorageItem(MASQUERADE_DISMISSED_KEY) === 'true',
    }) as NotificationState,
  getters: {
    getNotificationList: state => state.notificationList,
  },
  actions: {
    addNotification(payload) {
      const data = asConnectionToast(payload)
      // A repeated action (e.g. a second submit failing the same way) must not stack an
      // identical message on top of the one still on screen.
      const isAlreadyShown = this.notificationList.some(
        item =>
          item.type === data.type &&
          item.content === data.content &&
          item.description === (data.description ?? '') &&
          item.asToast === (data.asToast ?? false),
      )
      if (isAlreadyShown) {
        return
      }

      this.notificationCount++
      this.notificationList.push({
        id: this.notificationCount,
        type: data.type,
        content: data.content,
        description: data.description ?? '',
        withTimer: data.withTimer,
        asToast: data.asToast ?? false,
        keepOnNavigation: data.keepOnNavigation ?? false,
        isGlobal: data.isGlobal ?? false,
      })
    },
    removeNotification(id) {
      const index = this.notificationList.findIndex(item => item.id === id)
      if (this.notificationList[index]?.content === MASQUERADE_NOTIFICATION_CONTENT) {
        this.masqueradeNotificationDismissed = true
        setLocalStorageItem(MASQUERADE_DISMISSED_KEY, 'true')
      }
      this.notificationList.splice(index, 1)
    },
    /**
     * Called on each route change. A toast belongs to the page that raised it: left in the list,
     * the next page's BfToastViewport would mount it again with a fresh countdown. A toast flagged
     * `keepOnNavigation` survives this one navigation only; one flagged `isGlobal` belongs to no page
     * and is never cleared here. Banners are not touched.
     */
    clearToastsOnNavigation() {
      this.notificationList = this.notificationList.filter(
        item => !item.asToast || item.keepOnNavigation || item.isGlobal,
      )
      this.notificationList.forEach(item => {
        item.keepOnNavigation = false
      })
    },
    dismissMasqueradeNotification() {
      this.masqueradeNotificationDismissed = true
      setLocalStorageItem(MASQUERADE_DISMISSED_KEY, 'true')
    },
    resetMasqueradeNotificationDismissed() {
      this.masqueradeNotificationDismissed = false
      removeLocalStorageItem(MASQUERADE_DISMISSED_KEY)
    },
  },
})
